import { useEffect, useRef, type RefObject } from 'react';
import { gsap } from 'gsap';

/**
 * Organic light-orb shimmer effect using GSAP recursive random tweens.
 * Four desynchronized loops (x, y, size, intensity) with randomized
 * targets on every cycle — the combined pattern never repeats.
 *
 * Renders via CSS custom properties + radial-gradient on ::before.
 * The element needs `position: relative` and `overflow: hidden`.
 */
export function useOrbShimmer(ref: RefObject<HTMLElement | null>) {
  const tweens = useRef<gsap.core.Tween[]>([]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Seed initial custom properties
    gsap.set(el, {
      '--orb-x': '70%',
      '--orb-y': '40%',
      '--orb-size': '45%',
      '--orb-opacity': '0.55',
    });

    // --- Recursive drift: each call picks a fresh random target ---

    function driftX() {
      tweens.current.push(
        gsap.to(el, {
          '--orb-x': gsap.utils.random(5, 95) + '%',
          duration: gsap.utils.random(1.8, 4.2),
          ease: 'sine.inOut',
          onComplete: driftX,
        }),
      );
    }

    function driftY() {
      tweens.current.push(
        gsap.to(el, {
          '--orb-y': gsap.utils.random(10, 90) + '%',
          duration: gsap.utils.random(1.5, 3.8),
          ease: 'sine.inOut',
          onComplete: driftY,
        }),
      );
    }

    function breathe() {
      tweens.current.push(
        gsap.to(el, {
          '--orb-size': gsap.utils.random(30, 85) + '%',
          duration: gsap.utils.random(1.4, 3.2),
          ease: 'power1.inOut',
          onComplete: breathe,
        }),
      );
    }

    function pulse() {
      tweens.current.push(
        gsap.to(el, {
          '--orb-opacity': gsap.utils.random(0.3, 0.85),
          duration: gsap.utils.random(0.8, 2.4),
          ease: 'power2.inOut',
          onComplete: pulse,
        }),
      );
    }

    // Kick off all four independent loops
    driftX();
    driftY();
    breathe();
    pulse();

    return () => {
      tweens.current.forEach((t) => t.kill());
      tweens.current = [];
    };
  }, [ref]);
}
